import React, { useCallback, useMemo, useState } from 'react';
import { Image } from 'react-native';

import { useRoute } from '@react-navigation/native';
import { FlashList, type ListRenderItem } from '@shopify/flash-list';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { imgNotesEmpty } from '@/assets/images';
import { Scroll, Stack } from '@/components/layout';
import { CompactHeader } from '@/components/layout/app-header';
import { Loading, SvgIcon } from '@/components/ui';
import { STRINGS } from '@/constants/strings';
import { createStyles, useGuardianStackNavigation, useScaling } from '@/hooks';
import { guardianColors } from '@/theme/colors';

import GuardianScreenHeader from '@/features/guardian-player/components/guardian-screen-header';
import CoachNoteCard from '@/features/guardian-player/components/kit-notes/coach-note-card';
import KitClubsCard from '@/features/guardian-player/components/kit-notes/kit-clubs-card';
import KitNotesEmpty from '@/features/guardian-player/components/kit-notes/kit-notes-empty';
import KitNotesTabBar, {
  type KitNotesTab,
} from '@/features/guardian-player/components/kit-notes/kit-notes-tab-bar';
import type {
  KitNotesTabKey,
  PlayerCoachNote,
} from '@/features/guardian-player/components/kit-notes/types';
import { usePlayerKitNotes } from '@/features/guardian-player/hooks';

import type { GuardianStackScreenProps } from '@/navigations/types';

const strings = STRINGS.guardianPlayer.kitNotes;

const TABS: readonly KitNotesTab[] = [
  { key: 'coachNotes', label: strings.tabs.coachNotes },
  { key: 'kitClubs', label: strings.tabs.kitClubs },
];

const PlayerKitNotesScreen = () => {
  const navigation = useGuardianStackNavigation();
  const route = useRoute<GuardianStackScreenProps<'PlayerKitNotes'>['route']>();
  const styles = useStyles();
  const { scale, isTablet } = useScaling();
  const insets = useSafeAreaInsets();
  const { player } = route.params;

  const [activeTab, setActiveTab] = useState<KitNotesTabKey>('coachNotes');
  const { notes, kitClubs, isLoading } = usePlayerKitNotes(player);

  const firstName = player.firstname?.trim() || strings.fallbackPlayerName;
  const screenTitle = strings.screenTitle(firstName);

  const renderNote = useCallback<ListRenderItem<PlayerCoachNote>>(
    ({ item }) => <CoachNoteCard note={item} />,
    [],
  );

  const noteKeyExtractor = useCallback(
    (item: PlayerCoachNote, index: number) => `${item.coachName}-${item.date}-${index}`,
    [],
  );

  const notesEmptyComponent = useMemo(
    () =>
      isLoading ? (
        <Stack style={styles.loadingState}>
          <Loading />
        </Stack>
      ) : (
        <KitNotesEmpty
          illustration={
            <Image
              source={imgNotesEmpty}
              style={{ width: scale(161), height: scale(120) }}
              resizeMode="contain"
            />
          }
          message={strings.emptyNotes}
        />
      ),
    [isLoading, scale, styles.loadingState],
  );

  const bottomPadding = { paddingBottom: insets.bottom + scale(20) };

  return (
    <Stack
      flex={1}
      viewType={isTablet ? 'view' : 'gradient'}
      colors={['#EEF3FF', guardianColors['1'], '#EEF3FF']}
      locations={[0.08, 0.47, 0.91]}
      start={{ x: 1, y: 0 }}
      end={{ x: 0, y: 1 }}
      backgroundColor={isTablet ? guardianColors['1'] : undefined}>
      {isTablet ? (
        <GuardianScreenHeader title={screenTitle} onBack={navigation.goBack} />
      ) : (
        <CompactHeader title={screenTitle} onBack={navigation.goBack} />
      )}

      <KitNotesTabBar tabs={TABS} activeKey={activeTab} onChange={setActiveTab} />

      {activeTab === 'coachNotes' ? (
        <FlashList
          data={notes}
          renderItem={renderNote}
          keyExtractor={noteKeyExtractor}
          ItemSeparatorComponent={ItemSeparator}
          ListEmptyComponent={notesEmptyComponent}
          showsVerticalScrollIndicator={false}
          contentContainerStyle={[styles.listContent, bottomPadding]}
        />
      ) : (
        <Scroll
          showsVerticalScrollIndicator={false}
          contentContainerStyle={[styles.listContent, bottomPadding]}>
          {isLoading && kitClubs.length === 0 ? (
            <Stack style={styles.loadingState}>
              <Loading />
            </Stack>
          ) : kitClubs.length === 0 ? (
            <KitNotesEmpty
              illustration={<SvgIcon name="IcKitEmpty" width={scale(44)} height={scale(80)} />}
              message={strings.emptyKit}
            />
          ) : (
            <KitClubsCard clubs={kitClubs} />
          )}
        </Scroll>
      )}
    </Stack>
  );
};

function ItemSeparator() {
  const styles = useStyles();
  return <Stack style={styles.separator} />;
}

const useStyles = createStyles(({ scale, isTablet }) => ({
  listContent: {
    paddingHorizontal: scale(20),
    paddingTop: scale(16),
    width: isTablet ? '75%' : '100%',
    alignSelf: 'center',
  },
  separator: {
    height: scale(12),
  },
  loadingState: {
    paddingVertical: scale(64),
  },
}));

export default PlayerKitNotesScreen;
